import { useEffect, useMemo, useRef, useState } from 'react';
import { type ApiErrorResponse, MIN_SEARCH_PHRASE_LENGTH } from '../../../../../../../helpers';
import { normalizeSearchPhrase } from '../../../../../../../helpers/normalizeSearchPhrase';
import {
  useGetHierarchiesQuery,
  useSearchHierarchiesQuery,
  useSearchKnownHierarchiesQuery,
} from '../../../../../../../hooks/api';
import type { HierarchyItem } from '../../../../../../../lib/api/hierarchies';
import { HIERARCHY_SEARCH_LIMIT_ERROR } from '../consts';
import { type Alias, dictionaryAliasMappers, getQueryParams, mapCategoriesTree } from '../helpers';
import type { TaxonomyID, TreeNode, TreeNodeState } from '../types';

export const useTreeIncludeExclude = (
  clickedTaxonomyID: string,
  dictionary: 'MeSH' | 'IAB' = 'MeSH',
  initialState: {
    items: Alias[];
    mode?: Exclude<TreeNodeState, 'semi-allow' | 'semi-block'>;
  },
  searchPhrase?: string,
  advancedSearch = false,
) => {
  const [categoriesTree, setCategoriesTree] = useState<Record<TaxonomyID, TreeNode>>({});
  const [selectedTreeItems, setSelectedTreeItems] = useState<HierarchyItem[]>([]);
  const treeMode = useRef<Exclude<TreeNodeState, 'semi-allow' | 'semi-block'> | undefined>(initialState.mode);

  const queryParams = useMemo(() => getQueryParams(clickedTaxonomyID, dictionary), [clickedTaxonomyID, dictionary]);
  const { data, isFetching } = useGetHierarchiesQuery(queryParams, {
    enabled: !searchPhrase,
  });

  const {
    data: searchData,
    isFetching: isSearching,
    error,
    isError,
  } = useSearchHierarchiesQuery(
    {
      dictionary: `${dictionary}`,
      search: advancedSearch ? undefined : searchPhrase ? normalizeSearchPhrase(searchPhrase) : '',
      advancedSearch: advancedSearch ? searchPhrase : undefined,
      includeParents: true,
    },
    {
      enabled: !!searchPhrase && searchPhrase.length >= MIN_SEARCH_PHRASE_LENGTH,
    },
  );

  const { data: knownHierarchies } = useSearchKnownHierarchiesQuery(
    {
      dictionary: `${dictionary}`,
      taxonomies: selectedTreeItems.map((i) => i.taxonomy_id),
      includeParents: true,
    },
    { enabled: !!selectedTreeItems.length },
  );

  const { data: initialKnownHierarchies, isFetching: isFetchingInitialItems } = useSearchKnownHierarchiesQuery(
    {
      dictionary: `${dictionary}`,
      taxonomies: initialState.items.map((v) => dictionaryAliasMappers[dictionary](v)),
      includeParents: true,
    },
    { enabled: !!initialState.items.length },
  );

  const errorMessage = useMemo(() => {
    if (!isError) return '';

    const desc = (error?.data as ApiErrorResponse)?.details?.errorDesc;
    if (desc?.toLowerCase().includes('size')) {
      return HIERARCHY_SEARCH_LIMIT_ERROR;
    }
    return '';
  }, [error, isError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: relevant dependencies are added
  useEffect(() => {
    if (selectedTreeItems.length || !initialKnownHierarchies) {
      return;
    }

    const newSelectedItems = initialKnownHierarchies.filter((i) => initialState.items.includes(i.alias));

    const mode = treeMode.current;
    const semiMode = mode ? (`semi-${mode}` as TreeNodeState) : undefined;

    setCategoriesTree((currentTree) =>
      mapCategoriesTree(
        initialKnownHierarchies,
        currentTree,
        (node) => ({
          ...node,
          state: initialState.items.includes(node.alias) ? mode : semiMode,
        }),
        (category) => ({
          ...category,
          children: {},
          state: initialState.items.includes(category.alias) ? mode : semiMode,
        }),
      ),
    );

    setSelectedTreeItems((items) => items.concat(newSelectedItems));
  }, [initialKnownHierarchies, initialState.items]);

  // Tree item clicked
  useEffect(() => {
    if (!data) return;

    setCategoriesTree((currentTree) =>
      mapCategoriesTree(
        data,
        currentTree,
        (node, category) => ({
          ...node,
          isLoading: clickedTaxonomyID === category.taxonomy_id && isFetching,
        }),
        (category) => ({
          ...category,
          children: {},
        }),
      ),
    );
  }, [clickedTaxonomyID, data, isFetching]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: relevant dependencies are added
  useEffect(() => {
    if (isSearching) return;

    setCategoriesTree((currentTree) => {
      const newTree = JSON.parse(JSON.stringify(currentTree));

      const clearFlags = (nodes: Record<TaxonomyID, TreeNode>) => {
        Object.values(nodes).forEach((node) => {
          node.isSearchResult = false;
          node.isOpen = false;
          if (node.children) {
            clearFlags(node.children);
          }
        });
      };

      clearFlags(newTree);

      if (searchData && searchPhrase && searchPhrase.length >= MIN_SEARCH_PHRASE_LENGTH) {
        return mapCategoriesTree(
          searchData,
          newTree,
          (node) => ({ ...node, isSearchResult: true, isOpen: true }),
          (category) => ({
            ...category,
            children: {},
            isOpen: true,
            isSearchResult: true,
          }),
        );
      }

      return newTree;
    });
  }, [searchData, isSearching, searchPhrase, setCategoriesTree]);

  useEffect(() => {
    if (!knownHierarchies || !!initialKnownHierarchies) return;

    setCategoriesTree((currentTree) =>
      mapCategoriesTree(
        knownHierarchies ?? [],
        currentTree,
        (node) => node,
        (category) => ({
          ...category,
          children: {},
        }),
      ),
    );
  }, [knownHierarchies, initialKnownHierarchies]);

  return useMemo(
    () => ({
      categoriesTree,
      setCategoriesTree,

      selectedTreeItems,
      setSelectedTreeItems,

      isFetching: isFetching || isSearching || isFetchingInitialItems,
      treeMode,

      errorMessage,
    }),
    [categoriesTree, isFetchingInitialItems, isFetching, isSearching, selectedTreeItems, errorMessage],
  );
};
